import type { FC } from 'react'
import type { LegalSection } from '@/constants/legal'

import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { useScrollSpy } from '@/hooks/use-scroll-spy'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { Spark } from '@/components/kit/spark'

interface LegalPageProps {
    title: string
    intro: string
    updated: string
    sections: Array<LegalSection>
}

export const LegalPage: FC<LegalPageProps> = ({ title, intro, updated, sections }) => {
    const active = useScrollSpy(sections.map(section => section.id), '-20% 0px -65% 0px')

    return (
        <>
            <section
                aria-labelledby='legal-title'
                className='relative overflow-hidden pb-12 pt-36 md:pb-16 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />
                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel>The small print, made readable</SectionLabel>
                    <Reveal>
                        <h1
                            id='legal-title'
                            className='mt-6 text-[clamp(3rem,8vw,7rem)] font-bold leading-[0.88] tracking-[-0.04em]'
                        >
                            {title}
                        </h1>
                    </Reveal>
                    <Reveal
                        delay={150}
                        className='mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between'
                    >
                        <p className='max-w-xl text-lg leading-relaxed text-stone'>{intro}</p>
                        <p className='inline-flex items-center gap-2 self-start rounded-full border border-ink/10 bg-paper px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] md:self-auto'>
                            <span className='size-1.5 rounded-full bg-ember' />
                            Last updated{' '}
                            <time dateTime={updated}>{formatDate(updated)}</time>
                        </p>
                    </Reveal>
                </div>
            </section>

            <div className='mx-auto grid max-w-[1400px] gap-10 px-5 pb-10 md:px-10 lg:grid-cols-12'>
                <nav
                    aria-label='Sections'
                    className='lg:col-span-3'
                >
                    <div className='rounded-[1.5rem] border border-black/5 bg-paper p-5 lg:sticky lg:top-28 lg:border-0 lg:bg-transparent lg:p-0'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>On this page</p>
                        <ol className='mt-4 flex flex-col lg:border-l lg:border-ink/10'>
                            {sections.map((section, index) => {
                                const current = section.id === active

                                return (
                                    <li key={section.id}>
                                        <a
                                            href={`#${section.id}`}
                                            aria-current={current ? 'location' : undefined}
                                            className={cn(
                                                'flex gap-3 py-1.5 text-sm leading-snug transition-colors duration-300 lg:-ml-px lg:border-l-2 lg:py-2 lg:pl-4',
                                                current ? 'border-ember font-medium text-ink' : 'border-transparent text-stone hover:text-ink'
                                            )}
                                        >
                                            <span className='font-mono text-[11px] text-ink/40'>{String(index + 1).padStart(2, '0')}</span>
                                            {section.title}
                                        </a>
                                    </li>
                                )
                            })}
                        </ol>
                    </div>
                </nav>

                <div className='min-w-0 lg:col-span-7 lg:col-start-5'>
                    {sections.map((section, index) => (
                        <section
                            key={section.id}
                            id={section.id}
                            aria-labelledby={`${section.id}-title`}
                            className='scroll-mt-28 border-t border-ink/10 py-10 first:border-t-0 first:pt-0'
                        >
                            <p className='font-mono text-xs text-stone'>{String(index + 1).padStart(2, '0')}</p>
                            <h2
                                id={`${section.id}-title`}
                                className='mt-3 text-[clamp(1.75rem,3vw,2.4rem)] font-bold leading-[1.05] tracking-[-0.035em]'
                            >
                                {section.title}
                            </h2>
                            <div className='mt-5 flex flex-col gap-4 text-[17px] leading-relaxed text-ink/80'>
                                {section.body.map((block, blockIndex) => (
                                    typeof block === 'string' ? (
                                        <p key={blockIndex}>{block}</p>
                                    ) : (
                                        <ul
                                            key={blockIndex}
                                            className='flex flex-col gap-3'
                                        >
                                            {block.list.map(item => (
                                                <li
                                                    key={item}
                                                    className='flex items-start gap-3'
                                                >
                                                    <Spark className='mt-2 size-2.5 shrink-0 text-ember' />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </>
    )
}
